import JSZip from 'jszip';

import { apiBaseUrl, bearerHeader } from './api.js';
import { type ExternalizedAsset, externalizeInlineAssets } from './externalize-assets.js';

/**
 * Build an offline-first launcher bundle for a tenant.
 *
 * Pipeline (all client-side, no Worker round-trip):
 *   1. Fetch the latest launcher template ZIP from R2 (built by CI on
 *      every push to main — see .github/workflows/build-bundle.yml).
 *   2. Fetch the tenant's MobiLauncherConfig from the licensing-server.
 *   3. Inject config.json + license.json + manifest.json into the ZIP.
 *   4. Return a Blob ready for browser download as
 *      <tenantId>-launcher.zip.
 *
 * Errors surface as discriminated-union variants so the UI can render
 * tailored messages rather than introspect HTTP status codes.
 */

const TEMPLATE_URL =
  (import.meta.env['VITE_LAUNCHER_TEMPLATE_URL'] as string | undefined) ??
  'https://pub-02fbdf0f9b1441fb8c19d013db8aaf7d.r2.dev/launcher-templates/latest.zip';

export interface BundleManifest {
  tenantId: string;
  tier: 'core' | 'pro' | 'enterprise';
  assembledAt: string;
  /** Template version — for now, just "latest"; will pin a build sha when CI surfaces it. */
  templateRef: string;
  /** Number of inline assets externalized into `assets/` for this bundle. */
  assetCount: number;
  /** True when the bundle carries the server's Ed25519-signed manifest
   *  (device verifies integrity on boot); false for the unsigned fallback. */
  signed: boolean;
}

export type BundleResult =
  | { kind: 'ok'; blob: Blob; filename: string; manifest: BundleManifest }
  | { kind: 'no-config'; message: string }
  | { kind: 'invalid-config'; message: string }
  | { kind: 'template-failed'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; message: string };

export interface BuildBundleInput {
  tenantId: string;
  tier: 'core' | 'pro' | 'enterprise';
  /** The raw signed-license JSON string from the licensing-server. */
  rawLicenseBlob: string;
}

export async function buildBundle(input: BuildBundleInput): Promise<BundleResult> {
  // 1. Template
  let templateBlob: Blob;
  try {
    const res = await fetch(TEMPLATE_URL, { cache: 'no-store' });
    if (!res.ok) {
      return {
        kind: 'template-failed',
        message: `Template fetch failed: HTTP ${String(res.status)}`,
      };
    }
    templateBlob = await res.blob();
  } catch (err) {
    return {
      kind: 'network',
      message: err instanceof Error ? err.message : 'Could not reach the template bucket',
    };
  }

  // 2. Tenant config — required. The launcher renders an error screen
  //    if config.json is missing or malformed, so we surface this as a
  //    distinct failure mode for the UI ("publish a config first").
  let configJson: string;
  try {
    const res = await fetch(
      `${apiBaseUrl}/configs/${encodeURIComponent(input.tenantId)}`,
      { cache: 'no-store' },
    );
    if (res.status === 404) {
      return {
        kind: 'no-config',
        message: `No published config for tenant "${input.tenantId}". Publish one in the Builder first.`,
      };
    }
    if (!res.ok) {
      return {
        kind: 'network',
        message: `Config fetch failed: HTTP ${String(res.status)}`,
      };
    }
    configJson = await res.text();
  } catch (err) {
    return {
      kind: 'network',
      message: err instanceof Error ? err.message : 'Could not reach the config endpoint',
    };
  }

  // 2b. Validate the config parses (needed for either assembly path).
  let parsedConfig: unknown;
  try {
    parsedConfig = JSON.parse(configJson);
  } catch {
    return {
      kind: 'invalid-config',
      message: `Published config for "${input.tenantId}" is not valid JSON. Re-publish from the Builder.`,
    };
  }

  // 2c. Prefer a SIGNED bundle when the server can produce one: ship the
  //     canonical config + the server's Ed25519-signed manifest.json so the
  //     device verifies integrity on boot. Asset externalization rewrites
  //     config.json (data URLs → assets/ files), which would break that
  //     signature — so it runs ONLY on the unsigned fallback (no manifest
  //     endpoint reachable).
  const signedManifestJson = await fetchSignedManifest(input.tenantId);
  const signed = signedManifestJson !== null;
  const externalized = signed
    ? { config: parsedConfig, assets: [] as ExternalizedAsset[] }
    : externalizeInlineAssets(parsedConfig);
  // Signed path ships the exact bytes the server hashed; unsigned path ships
  // the externalized rewrite.
  const finalConfigJson = signed ? configJson : JSON.stringify(externalized.config);

  // 3. Assemble. Load the template into JSZip, read its embedded
  //    version.txt (baked by CI; absent on hand-built templates),
  //    drop in the per-tenant files alongside index.html.
  let outBlob: Blob;
  let manifest: BundleManifest;
  try {
    const zip = await JSZip.loadAsync(templateBlob);
    const versionEntry = zip.file('version.txt');
    const templateRef =
      versionEntry !== null ? (await versionEntry.async('string')).trim() : 'unknown';

    manifest = {
      tenantId: input.tenantId,
      tier: input.tier,
      assembledAt: new Date().toISOString(),
      templateRef,
      assetCount: externalized.assets.length,
      signed,
    };

    for (const asset of externalized.assets) {
      zip.file(asset.path, asset.base64, { base64: true });
    }
    zip.file('config.json', finalConfigJson);
    // Emit license.json whenever we have one; the engine fail-softs to Core
    // when absent. The signed manifest covers it when the tenant has an
    // active license, so it must match the bytes the server hashed.
    if (input.rawLicenseBlob.length > 0) {
      zip.file('license.json', input.rawLicenseBlob);
    }
    // Signed bundle → the server's signed manifest; otherwise the local
    // (unsigned, informational) manifest. The renderer treats a manifest
    // without a signature as "unsigned, boot normally".
    zip.file('manifest.json', signedManifestJson ?? JSON.stringify(manifest, null, 2));
    zip.file('README.txt', renderReadme(manifest));
    outBlob = await zip.generateAsync({ type: 'blob' });
  } catch (err) {
    return {
      kind: 'unexpected',
      message: err instanceof Error ? err.message : 'ZIP assembly failed',
    };
  }

  return {
    kind: 'ok',
    blob: outBlob,
    filename: `${sanitizeForFilename(input.tenantId)}-launcher.zip`,
    manifest,
  };
}

/**
 * Trigger a browser download for an assembled bundle. Object URL is
 * revoked after a short timeout — long enough for the browser to start
 * the download, short enough to free memory on big bundles.
 */
export function downloadBundle(result: Extract<BundleResult, { kind: 'ok' }>): void {
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Fetch the server's Ed25519-signed bundle manifest for a tenant. Returns
 * the raw JSON text (embedded verbatim as manifest.json) when it's a
 * structurally-valid signed manifest, or null on any failure — in which
 * case buildBundle falls back to an unsigned, asset-externalized bundle.
 * The client doesn't verify the signature (no key); the device does.
 */
async function fetchSignedManifest(tenantId: string): Promise<string | null> {
  try {
    const res = await fetch(
      `${apiBaseUrl}/bundle/${encodeURIComponent(tenantId)}/manifest`,
      { cache: 'no-store' },
    );
    if (!res.ok) return null;
    const text = await res.text();
    const parsed = JSON.parse(text) as { signature?: unknown; files?: unknown };
    if (typeof parsed.signature === 'string' && Array.isArray(parsed.files)) {
      return text;
    }
    return null;
  } catch {
    return null;
  }
}

/** Outcome of publishing a built bundle to the hosted-bundle endpoint. */
export type PublishHostedResult =
  | { kind: 'ok'; downloadUrl: string; downloadToken: string }
  | { kind: 'unauthorized'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; message: string };

/**
 * POST an assembled bundle ZIP to `/tenant/bundle/:tenantId` (edit-token
 * auth). The server stores it privately and returns a stable, tokened
 * download URL the customer can point a file-server sync job at.
 */
export async function publishHostedBundle(input: {
  tenantId: string;
  /** Owner/admin authorize via the session cookie; null unless a machine token
   *  is explicitly supplied. */
  editToken: string | null;
  blob: Blob;
}): Promise<PublishHostedResult> {
  let res: Response;
  try {
    res = await fetch(`${apiBaseUrl}/tenant/bundle/${encodeURIComponent(input.tenantId)}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...bearerHeader(input.editToken),
        'content-type': 'application/zip',
      },
      body: input.blob,
    });
  } catch (err) {
    return { kind: 'network', message: err instanceof Error ? err.message : 'network error' };
  }
  if (res.status === 401) {
    return { kind: 'unauthorized', message: 'Edit token rejected — reload the Portal and retry.' };
  }
  if (res.status === 201) {
    const body = (await safeJson(res)) as { downloadUrl?: unknown; downloadToken?: unknown };
    if (typeof body.downloadUrl === 'string' && typeof body.downloadToken === 'string') {
      return { kind: 'ok', downloadUrl: body.downloadUrl, downloadToken: body.downloadToken };
    }
    return { kind: 'unexpected', message: 'Server returned 201 but a malformed body.' };
  }
  return { kind: 'unexpected', message: `Unexpected status ${String(res.status)}.` };
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

function sanitizeForFilename(s: string): string {
  return s.replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'tenant';
}

function renderReadme(m: BundleManifest): string {
  return [
    'MobiLauncher offline bundle',
    '===========================',
    '',
    `Tenant:        ${m.tenantId}`,
    `Tier:          ${m.tier}`,
    `Assembled at:  ${m.assembledAt}`,
    `Template ref:  ${m.templateRef}`,
    `Bundled assets: ${String(m.assetCount)} (under assets/)`,
    `Integrity:     ${m.signed ? 'signed (device verifies manifest.json on boot)' : 'unsigned'}`,
    '',
    'Deployment (any kiosk-capable MDM):',
    '',
    '1. Unzip this archive into a folder you can push to devices via',
    '   your MDM file-sync / sideload payload (typical destinations:',
    '   /sdcard/mobilauncher/ on Android, or whatever path your MDM',
    '   prefers — SOTI File Sync, Intune managed configurations,',
    '   Workspace ONE Resources, Scalefusion File Distribution, etc.).',
    '2. In the MDM admin console, create or edit your kiosk-mode',
    '   browser profile (Lockdown Profile in SOTI; Multi-app Kiosk',
    '   in Intune; Browser Kiosk in Scalefusion; the equivalent in',
    '   yours).',
    '3. Set the kiosk WebView URL to:',
    '',
    `      file:///<path-to-folder>/index.html`,
    '',
    '   The exact path depends on where your MDM drops the folder on',
    '   the device.',
    '4. Push the profile to your devices. They will boot into the',
    '   launcher with no further configuration.',
    '',
    'To update the config:',
    '  Edit your config in the Builder, click Publish, then download a',
    '  fresh bundle from the Portal and re-push the folder. Devices',
    '  with the Enterprise remoteConfig.poll feature will pick up new',
    '  configs automatically.',
    '',
    'Verifying the bundle (optional):',
    `  cat manifest.json`,
    '',
  ].join('\n');
}
